import React, { useState, useEffect, useMemo } from "react";
import { billPaymentApi } from "service/vizoBillPayment";
import { fetchWrapper } from "utils/apiFetch";

// Credit card issuer presets
const CREDIT_CARD_OPERATORS = [
  { opcode: "HDFC_CC", name: "HDFC Bank Credit Card" },
  { opcode: "SBI_CC", name: "SBI Credit Card" },
  { opcode: "ICICI_CC", name: "ICICI Bank Credit Card" },
  { opcode: "AXIS_CC", name: "Axis Bank Credit Card" },
  { opcode: "KOTAK_CC", name: "Kotak Mahindra Credit Card" },
  { opcode: "AMEX_CC", name: "American Express Credit Card" },
  { opcode: "OTHER_CC", name: "Other Bank Credit Card" },
];

const getCategoryIcon = (catName = "") => {
  const name = catName.toLowerCase();
  if (name.includes("broadband")) return "ph-wifi-high";
  if (name.includes("cable")) return "ph-television";
  if (name.includes("credit")) return "ph-credit-card";
  if (name.includes("dth")) return "ph-broadcast";
  if (name.includes("voucher") || name.includes("digital")) return "ph-ticket";
  if (name.includes("donation")) return "ph-heart";
  if (name.includes("electricity")) return "ph-lightning";
  if (name.includes("fastag")) return "ph-car";
  if (name.includes("lpg")) return "ph-cooking-pot";
  if (name.includes("gas")) return "ph-fire";
  if (name.includes("insurance")) return "ph-shield-check";
  if (name.includes("landline")) return "ph-phone-call";
  if (name.includes("loan")) return "ph-bank";
  if (name.includes("mobile")) return "ph-device-mobile";
  if (name.includes("municipal") || name.includes("tax")) return "ph-buildings";
  if (name.includes("pan")) return "ph-identification-card";
  if (name.includes("postpaid")) return "ph-article";
  if (name.includes("water")) return "ph-drop";
  return "ph-dots-three-circle";
};

const getOpcode = (op) => {
  if (!op) return "";
  if (typeof op === "string") return op;
  return op.code || op.opcode || op.operator_code || op.id || op.operatorName || op.name || op.display_name || "";
};

const getOperatorName = (op) => {
  if (!op) return "";
  if (typeof op === "string") return op;
  const name = op.name || op.display_name || op.operatorName || "";
  const code = op.code || op.opcode || op.operator_code || op.id || "";
  if (name && code && name !== code) {
    return `${name} (${code})`;
  }
  return name || code || "";
};

function BillPayment() {
  const [activeTab, setActiveTab] = useState("bbps"); // 'bbps' | 'credit-card' | 'status'

  // BBPS State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [opSearchTerm, setOpSearchTerm] = useState("");
  const [isOpDropdownOpen, setIsOpDropdownOpen] = useState(false);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const filteredOperators = useMemo(() => {
    if (!opSearchTerm || opSearchTerm.trim() === "") return operators;
    const term = opSearchTerm.toLowerCase().trim();
    return operators.filter((op) => {
      const name = getOperatorName(op).toLowerCase();
      const code = getOpcode(op).toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [operators, opSearchTerm]);

  // Credit Card State
  const [cardNumber, setCardNumber] = useState("");
  const [cardIssuerOpcode, setCardIssuerOpcode] = useState("HDFC_CC");
  const [cardMobile, setCardMobile] = useState("");
  const [ccSearchTerm, setCcSearchTerm] = useState("");
  const [isCcDropdownOpen, setIsCcDropdownOpen] = useState(false);

  const filteredCcOperators = useMemo(() => {
    if (!ccSearchTerm || ccSearchTerm.trim() === "") return CREDIT_CARD_OPERATORS;
    const term = ccSearchTerm.toLowerCase().trim();
    return CREDIT_CARD_OPERATORS.filter((op) => op.name.toLowerCase().includes(term));
  }, [ccSearchTerm]);

  // Common UI State
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fetchedBill, setFetchedBill] = useState(null); // Bill preview
  const [txnPin, setTxnPin] = useState("");
  const [showPinModal, setShowPinModal] = useState(false);
  const [receipt, setReceipt] = useState(null); // Payment result receipt

  // Status Check State
  const [statusOrderId, setStatusOrderId] = useState("");
  const [statusResult, setStatusResult] = useState(null);

  // Toast notification helper
  const showToast = (message, type = "primary") => {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible position-fixed top-0 end-0 m-3 shadow`;
    alertDiv.style.zIndex = 3000;
    alertDiv.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0 flex-grow-1">${message}</p>
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      </div>`;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv?.remove(), 3500);
  };

  // 1. Initial Load: Fetch BBPS Categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await billPaymentApi.getCategories();
        let catList = [];
        if (res?.data?.categories && Array.isArray(res.data.categories)) {
          catList = res.data.categories;
        } else if (Array.isArray(res?.categories)) {
          catList = res.categories;
        } else if (Array.isArray(res?.data)) {
          catList = res.data;
        }

        if (catList.length > 0) {
          setCategories(catList);
        } else {
          // Fallback legacy operator categories if endpoint is empty
          const fallbackRes = await fetchWrapper("https://pro.mokshiq.in/fetch_bbps1_operator?userid=3");
          const opData = Array.isArray(fallbackRes) ? fallbackRes : Array.isArray(fallbackRes?.data) ? fallbackRes.data : [];
          if (opData.length > 0) {
            setOperators(opData);
            const uniqueCats = [...new Set(opData.map((op) => op.category || op.category_name).filter(Boolean))];
            setCategories(uniqueCats);
          }
        }
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    };
    loadCategories();
  }, []);

  // 2. Fetch Operators when BBPS category changes
  const handleCategorySelect = async (cat) => {
    setSelectedCategory(cat);
    setSelectedOperator(null);
    setOpSearchTerm("");
    setIsOpDropdownOpen(false);
    setFetchedBill(null);
    setErrorMessage("");
    setLoading(true);

    try {
      const res = await billPaymentApi.getOperators(cat);
      let opList = [];
      if (res?.data?.operators && Array.isArray(res.data.operators)) {
        opList = res.data.operators;
      } else if (Array.isArray(res?.data)) {
        opList = res.data;
      } else if (Array.isArray(res)) {
        opList = res;
      }

      if (opList.length > 0) {
        setOperators(opList);
      } else {
        // Fallback filter from operators list
        const fallbackRes = await fetchWrapper("https://pro.mokshiq.in/fetch_bbps1_operator?userid=3");
        const allOps = Array.isArray(fallbackRes) ? fallbackRes : Array.isArray(fallbackRes?.data) ? fallbackRes.data : [];
        const filtered = allOps.filter((op) => {
          const opCat = String(op.category || op.category_name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
          const selCat = String(cat).toLowerCase().replace(/[^a-z0-9]/g, "");
          return opCat === selCat || opCat.includes(selCat) || selCat.includes(opCat);
        });
        setOperators(filtered.length > 0 ? filtered : allOps);
      }
    } catch (err) {
      console.error("Error fetching operators:", err);
      setErrorMessage("Could not load operators for selected category.");
    } finally {
      setLoading(false);
    }
  };

  // 3. Execute BBPS Bill Fetch
  const handleFetchBbpsBill = async (e) => {
    if (e) e.preventDefault();
    if (!selectedOperator) return setErrorMessage("Please select an operator");
    if (!consumerNumber.trim()) return setErrorMessage("Consumer number is required");
    if (!mobileNumber.trim()) return setErrorMessage("Customer mobile number is required");

    setErrorMessage("");
    setFetchedBill(null);
    setLoading(true);

    try {
      const res = await billPaymentApi.fetchBbpsBill({
        mobile: mobileNumber.trim(),
        opcode: getOpcode(selectedOperator),
        consumerNumber: consumerNumber.trim(),
      });

      if (res?.status === "success" || res?.status === true) {
        setFetchedBill(res.data || res);
        showToast("Bill fetched successfully", "success");
      } else {
        setErrorMessage(res?.message || "Failed to fetch bill details.");
      }
    } catch (err) {
      console.error("Bill fetch error:", err);
      setErrorMessage(err?.data?.message || err?.message || "Error fetching bill details.");
    } finally {
      setLoading(false);
    }
  };

  // 4. Execute Credit Card Bill Fetch
  const handleFetchCreditCardBill = async (e) => {
    if (e) e.preventDefault();
    if (!cardNumber.trim() || cardNumber.replace(/\D/g, "").length < 15) {
      return setErrorMessage("Please enter a valid 15 or 16 digit card number.");
    }
    if (!cardMobile.trim() || cardMobile.length < 10) {
      return setErrorMessage("Please enter a valid 10-digit mobile number.");
    }

    setErrorMessage("");
    setFetchedBill(null);
    setLoading(true);

    try {
      const res = await billPaymentApi.fetchCreditCardBill({
        mobile: cardMobile.trim(),
        card: cardNumber.trim(),
        opcode: cardIssuerOpcode,
      });

      if (res?.status === "success" || res?.status === true) {
        setFetchedBill(res.data || res);
        showToast("Credit card bill fetched successfully", "success");
      } else {
        setErrorMessage(res?.message || "Credit card bill fetch failed.");
      }
    } catch (err) {
      console.error("Credit card fetch error:", err);
      setErrorMessage(err?.data?.message || err?.message || "Network error fetching credit card bill.");
    } finally {
      setLoading(false);
    }
  };

  // 5. Execute Payment (Authorized via T-PIN)
  const handlePayBill = async () => {
    if (!txnPin || txnPin.length < 4) {
      setErrorMessage("Please enter a valid transaction PIN");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setShowPinModal(false);

    try {
      let res;
      if (activeTab === "bbps") {
        res = await billPaymentApi.payBbpsBill({
          mobile: mobileNumber.trim(),
          opcode: getOpcode(selectedOperator),
          consumerNumber: consumerNumber.trim(),
          amount: fetchedBill?.bill_amount || fetchedBill?.due_amount || fetchedBill?.amount || 0,
          fetchId: fetchedBill?.fetch_id || fetchedBill?.reference || "",
          pin: txnPin,
        });
      } else {
        res = await billPaymentApi.payCreditCardBill({
          mobile: cardMobile.trim(),
          card: cardNumber.trim(),
          amount: fetchedBill?.bill_amount || fetchedBill?.due_amount || fetchedBill?.amount || 0,
          fetchId: fetchedBill?.fetch_id || fetchedBill?.reference || "",
          opcode: cardIssuerOpcode,
          pin: txnPin,
        });
      }

      if (res?.status === "success" || res?.status === "pending" || res?.status === true) {
        setReceipt({
          ...res?.data,
          status: res?.status === true ? "success" : res?.status,
          message: res?.message || "Payment processed successfully.",
        });
        setFetchedBill(null);
        showToast(res?.message || "Payment submitted successfully", "success");
      } else {
        setErrorMessage(res?.message || "Payment failed.");
      }
    } catch (err) {
      console.error("Payment error:", err);
      setErrorMessage(err?.data?.message || err?.message || "Failed to submit payment request.");
    } finally {
      setLoading(false);
      setTxnPin("");
    }
  };

  // 6. Check Transaction Status
  const handleCheckStatus = async (e) => {
    if (e) e.preventDefault();
    if (!statusOrderId.trim()) {
      return setErrorMessage("Please enter an Order ID");
    }

    setLoading(true);
    setErrorMessage("");
    setStatusResult(null);

    try {
      const res = await billPaymentApi.checkStatus(statusOrderId.trim());
      if (res?.status === "success" || res?.status === true) {
        setStatusResult(res.data || res);
      } else {
        setErrorMessage(res?.message || "Status check failed.");
      }
    } catch (err) {
      console.error("Status check error:", err);
      setErrorMessage(err?.data?.message || err?.message || "Error checking order status.");
    } finally {
      setLoading(false);
    }
  };

  const money = (v) =>
    Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <main>
      <div className="container-fluid">
        {/* Breadcrumbs Header */}
        <div className="row m-1">
          <div className="col-12">
            <h4 className="main-title">Bill Payment Portal</h4>
            <ul className="app-line-breadcrumbs mb-3">
              <li>
                <a className="f-s-14 f-w-500" href="#">
                  <span>
                    <i className="ph-duotone ph-briefcase-metal f-s-16"></i> Services
                  </span>
                </a>
              </li>
              <li className="active">
                <a className="f-s-14 f-w-500" href="#">Bill Payment</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              {/* Overlay Loader */}
              {loading && (
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 4000 }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              <div className="card-body">
                {/* Interactive 3D Feature Cards Navigation Header */}
                <div className="row g-3 mb-4">
                  {/* Card 1: BBPS Utilities */}
                  <div className="col-12 col-md-4">
                    <div
                      className="p-3 rounded-4 transition-all position-relative overflow-hidden"
                      style={{
                        background: activeTab === "bbps"
                          ? "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)"
                          : "#ffffff",
                        color: activeTab === "bbps" ? "#ffffff" : "#1e293b",
                        border: activeTab === "bbps" ? "none" : "1px solid #e2e8f0",
                        boxShadow: activeTab === "bbps"
                          ? "0 10px 25px rgba(124, 58, 237, 0.35)"
                          : "0 2px 8px rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      onClick={() => {
                        setActiveTab("bbps");
                        setReceipt(null);
                        setFetchedBill(null);
                        setErrorMessage("");
                      }}
                      onMouseEnter={(e) => {
                        if (activeTab !== "bbps") {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.borderColor = "#c4b5fd";
                          e.currentTarget.style.boxShadow = "0 8px 20px rgba(124, 58, 237, 0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeTab !== "bbps") {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                        }
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center me-3 rounded-3"
                            style={{
                              width: "42px",
                              height: "42px",
                              background: activeTab === "bbps" ? "rgba(255, 255, 255, 0.2)" : "#f3e8ff",
                              color: activeTab === "bbps" ? "#ffffff" : "#7c3aed",
                            }}
                          >
                            <i className="ph-duotone ph-lightning f-s-22" />
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0 f-s-14">BBPS Utilities</h6>
                            <span
                              className="badge mt-1 fw-medium px-2 py-0.5"
                              style={{
                                fontSize: "10px",
                                background: activeTab === "bbps" ? "rgba(255, 255, 255, 0.25)" : "#ede9fe",
                                color: activeTab === "bbps" ? "#ffffff" : "#6d28d9",
                              }}
                            >
                              Instant Fetch
                            </span>
                          </div>
                        </div>
                        {activeTab === "bbps" && (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center ms-2"
                            style={{ width: "22px", height: "22px", background: "rgba(255, 255, 255, 0.3)" }}
                          >
                            <i className="ph-bold ph-check text-white f-s-12" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Credit Card Bill */}
                  <div className="col-12 col-md-4">
                    <div
                      className="p-3 rounded-4 transition-all position-relative overflow-hidden"
                      style={{
                        background: activeTab === "credit-card"
                          ? "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)"
                          : "#ffffff",
                        color: activeTab === "credit-card" ? "#ffffff" : "#1e293b",
                        border: activeTab === "credit-card" ? "none" : "1px solid #e2e8f0",
                        boxShadow: activeTab === "credit-card"
                          ? "0 10px 25px rgba(124, 58, 237, 0.35)"
                          : "0 2px 8px rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      onClick={() => {
                        setActiveTab("credit-card");
                        setReceipt(null);
                        setFetchedBill(null);
                        setErrorMessage("");
                      }}
                      onMouseEnter={(e) => {
                        if (activeTab !== "credit-card") {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.borderColor = "#c4b5fd";
                          e.currentTarget.style.boxShadow = "0 8px 20px rgba(124, 58, 237, 0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeTab !== "credit-card") {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                        }
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center me-3 rounded-3"
                            style={{
                              width: "42px",
                              height: "42px",
                              background: activeTab === "credit-card" ? "rgba(255, 255, 255, 0.2)" : "#e0e7ff",
                              color: activeTab === "credit-card" ? "#ffffff" : "#4f46e5",
                            }}
                          >
                            <i className="ph-duotone ph-credit-card f-s-22" />
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0 f-s-14">Credit Card Bill</h6>
                            <span
                              className="badge mt-1 fw-medium px-2 py-0.5"
                              style={{
                                fontSize: "10px",
                                background: activeTab === "credit-card" ? "rgba(255, 255, 255, 0.25)" : "#e0e7ff",
                                color: activeTab === "credit-card" ? "#ffffff" : "#3730a3",
                              }}
                            >
                              All Banks
                            </span>
                          </div>
                        </div>
                        {activeTab === "credit-card" && (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center ms-2"
                            style={{ width: "22px", height: "22px", background: "rgba(255, 255, 255, 0.3)" }}
                          >
                            <i className="ph-bold ph-check text-white f-s-12" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Status Check */}
                  <div className="col-12 col-md-4">
                    <div
                      className="p-3 rounded-4 transition-all position-relative overflow-hidden"
                      style={{
                        background: activeTab === "status"
                          ? "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)"
                          : "#ffffff",
                        color: activeTab === "status" ? "#ffffff" : "#1e293b",
                        border: activeTab === "status" ? "none" : "1px solid #e2e8f0",
                        boxShadow: activeTab === "status"
                          ? "0 10px 25px rgba(124, 58, 237, 0.35)"
                          : "0 2px 8px rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      onClick={() => {
                        setActiveTab("status");
                        setReceipt(null);
                        setFetchedBill(null);
                        setErrorMessage("");
                      }}
                      onMouseEnter={(e) => {
                        if (activeTab !== "status") {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.borderColor = "#c4b5fd";
                          e.currentTarget.style.boxShadow = "0 8px 20px rgba(124, 58, 237, 0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeTab !== "status") {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                        }
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center me-3 rounded-3"
                            style={{
                              width: "42px",
                              height: "42px",
                              background: activeTab === "status" ? "rgba(255, 255, 255, 0.2)" : "#dcfce7",
                              color: activeTab === "status" ? "#ffffff" : "#16a34a",
                            }}
                          >
                            <i className="ph-duotone ph-clock-counter-clockwise f-s-22" />
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0 f-s-14">Status Check</h6>
                            <span
                              className="badge mt-1 fw-medium px-2 py-0.5"
                              style={{
                                fontSize: "10px",
                                background: activeTab === "status" ? "rgba(255, 255, 255, 0.25)" : "#dcfce7",
                                color: activeTab === "status" ? "#ffffff" : "#15803d",
                              }}
                            >
                              Real-time Sync
                            </span>
                          </div>
                        </div>
                        {activeTab === "status" && (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center ms-2"
                            style={{ width: "22px", height: "22px", background: "rgba(255, 255, 255, 0.3)" }}
                          >
                            <i className="ph-bold ph-check text-white f-s-12" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Banner */}
                {errorMessage && (
                  <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
                    <i className="ph-duotone ph-warning-circle me-2 f-s-16" />
                    {errorMessage}
                    <button type="button" className="btn-close" onClick={() => setErrorMessage("")}></button>
                  </div>
                )}

                {/* RECEIPT VIEW */}
                {receipt && (
                  <div className={`alert ${receipt.status === "success" ? "alert-success border-success" : "alert-warning border-warning"} p-4 rounded mb-4 shadow-sm`}>
                    <h5 className="alert-heading d-flex align-items-center mb-3">
                      <i className={`ph-duotone ${receipt.status === "success" ? "ph-check-circle text-success" : "ph-clock text-warning"} me-2 f-s-24`} />
                      {receipt.status === "success" ? "Payment Successful" : "Payment Pending / Processing"}
                    </h5>
                    <p className="mb-3 f-s-14">{receipt.message}</p>
                    <hr />
                    <div className="row g-2 f-s-14">
                      <div className="col-md-6"><strong>Order ID:</strong> {receipt.orderid || receipt.order_id || "N/A"}</div>
                      <div className="col-md-6"><strong>UTR / Ref:</strong> {receipt.utr || receipt.api_txn_id || "Pending"}</div>
                      <div className="col-md-6"><strong>TxID:</strong> {receipt.txid || "N/A"}</div>
                      <div className="col-md-6"><strong>Amount Paid:</strong> ₹{money(receipt.amount)}</div>
                      {receipt.wallet_balance !== undefined && (
                        <div className="col-md-6"><strong>Wallet Balance:</strong> ₹{money(receipt.wallet_balance)}</div>
                      )}
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn btn-sm text-white"
                        style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)", borderRadius: "8px" }}
                        onClick={() => {
                          setReceipt(null);
                          setFetchedBill(null);
                          setConsumerNumber("");
                          setCardNumber("");
                        }}
                      >
                        Make Another Payment
                      </button>
                    </div>
                  </div>
                )}

                {/* TAB 1: BBPS UTILITIES */}
                {activeTab === "bbps" && !receipt && (
                  <form onSubmit={handleFetchBbpsBill} className="app-form">
                    <div className="row">
                      {/* Step 1: Category Chips / Selection */}
                      <div className="col-12 mb-4">
                        <label className="form-label fw-bold text-dark f-s-14">1. Select Service Category</label>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {categories.map((cat, idx) => {
                            const isSelected = selectedCategory === cat;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleCategorySelect(cat)}
                                className="btn d-inline-flex align-items-center justify-content-center px-3 py-1.5 transition-all"
                                style={{
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  height: "36px",
                                  border: isSelected ? "1px solid #7c3aed" : "1px solid #e0e7ff",
                                  background: isSelected
                                    ? "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)"
                                    : "#f8f9ff",
                                  color: isSelected ? "#ffffff" : "#4c1d95",
                                  boxShadow: isSelected
                                    ? "0 4px 12px rgba(124, 58, 237, 0.35)"
                                    : "0 1px 2px rgba(0,0,0,0.03)",
                                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                  cursor: "pointer",
                                  flex: "0 0 auto",
                                  width: "auto",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = "#ede9fe";
                                    e.currentTarget.style.borderColor = "#c4b5fd";
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 10px rgba(124, 58, 237, 0.15)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = "#f8f9ff";
                                    e.currentTarget.style.borderColor = "#e0e7ff";
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
                                  }
                                }}
                              >
                                <i className={`${getCategoryIcon(cat)} me-1.5 f-s-14 flex-shrink-0`} />
                                <span className="text-nowrap">{cat}</span>
                                {isSelected && <i className="ph-bold ph-check ms-1.5 f-s-12 text-white" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Step 2: Operator Dropdown with Search */}
                      {selectedCategory && (
                        <div className="col-md-6 mb-3 position-relative">
                          <label className="form-label fw-bold text-dark f-s-14">2. Select Biller / Operator</label>
                          <div className="position-relative">
                            <i
                              className="ph-duotone ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted f-s-16"
                              style={{ pointerEvents: "none" }}
                            />
                            <input
                              type="text"
                              className="form-control shadow-sm ps-5 pe-5"
                              style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                              placeholder="Type to search or choose operator..."
                              value={isOpDropdownOpen ? opSearchTerm : (selectedOperator ? getOperatorName(selectedOperator) : opSearchTerm)}
                              onFocus={() => setIsOpDropdownOpen(true)}
                              onChange={(e) => {
                                setOpSearchTerm(e.target.value);
                                setIsOpDropdownOpen(true);
                                if (!e.target.value) {
                                  setSelectedOperator(null);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setIsOpDropdownOpen(false), 200);
                              }}
                              required
                            />
                            <span
                              className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                              style={{ fontSize: "11px", pointerEvents: "none" }}
                            >
                              ▼
                            </span>
                          </div>

                          {isOpDropdownOpen && (
                            <div
                              className="position-absolute start-0 end-0 shadow-lg overflow-auto mt-1"
                              style={{
                                maxHeight: "220px",
                                zIndex: 1050,
                                borderRadius: "10px",
                                background: "#ffffff",
                                border: "1px solid #cbd5e1",
                                left: "12px",
                                right: "12px",
                              }}
                            >
                              {filteredOperators.length === 0 ? (
                                <div className="px-3 py-2 text-muted f-s-13">No matching operators found</div>
                              ) : (
                                filteredOperators.map((op, i) => {
                                  const name = getOperatorName(op);
                                  const isSel = selectedOperator && (getOpcode(selectedOperator) === getOpcode(op) || getOperatorName(selectedOperator) === name);
                                  return (
                                    <div
                                      key={i}
                                      className={`px-3 py-2 text-wrap f-s-13 border-bottom ${isSel ? "bg-primary text-white fw-bold" : "text-dark"}`}
                                      style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                      onMouseDown={() => {
                                        setSelectedOperator(op);
                                        setOpSearchTerm(name);
                                        setIsOpDropdownOpen(false);
                                        setFetchedBill(null);
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSel) {
                                          e.currentTarget.style.background = "#ede9fe";
                                          e.currentTarget.style.borderLeft = "3px solid #7c3aed";
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSel) {
                                          e.currentTarget.style.background = "transparent";
                                          e.currentTarget.style.borderLeft = "none";
                                        }
                                      }}
                                    >
                                      {name}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 3: Inputs */}
                      {selectedOperator && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold text-dark f-s-14">Consumer Number / Account ID</label>
                            <input
                              type="text"
                              className="form-control shadow-sm"
                              style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                              placeholder="Enter Consumer Number / Account ID"
                              value={consumerNumber}
                              onChange={(e) => setConsumerNumber(e.target.value)}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold text-dark f-s-14">Customer Mobile Number</label>
                            <input
                              type="tel"
                              className="form-control shadow-sm"
                              style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                              maxLength="10"
                              placeholder="Enter 10-digit Mobile Number"
                              value={mobileNumber}
                              onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ""))}
                              required
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {selectedOperator && (
                      <div className="d-flex justify-content-center mt-4 w-100">
                        <button
                          type="submit"
                          className="btn text-white px-4 py-2.5 fw-semibold"
                          style={{
                            background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                            borderRadius: "10px",
                            boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                            transition: "all 0.2s ease-in-out",
                            width: "auto",
                            minWidth: "180px",
                            maxWidth: "240px",
                            display: "inline-block",
                          }}
                          onMouseEnter={(e) => {
                            if (!loading && consumerNumber && mobileNumber) {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 20px rgba(124, 58, 237, 0.45)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(124, 58, 237, 0.3)";
                          }}
                          disabled={loading || !consumerNumber || !mobileNumber}
                        >
                          {loading ? "Fetching Bill..." : "Fetch Bill Details"}
                        </button>
                      </div>
                    )}
                  </form>
                )}

                {/* TAB 2: CREDIT CARD BILL */}
                {activeTab === "credit-card" && !receipt && (
                  <form onSubmit={handleFetchCreditCardBill} className="app-form">
                    <div className="row">
                      <div className="col-md-6 mb-3 position-relative">
                        <label className="form-label fw-bold text-dark f-s-14">Card Issuer / Bank</label>
                        <div className="position-relative">
                          <i
                            className="ph-duotone ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted f-s-16"
                            style={{ pointerEvents: "none" }}
                          />
                          <input
                            type="text"
                            className="form-control shadow-sm ps-5 pe-5"
                            style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                            placeholder="Type to search or choose card issuer..."
                            value={isCcDropdownOpen ? ccSearchTerm : (CREDIT_CARD_OPERATORS.find((o) => o.opcode === cardIssuerOpcode)?.name || ccSearchTerm)}
                            onFocus={() => setIsCcDropdownOpen(true)}
                            onChange={(e) => {
                              setCcSearchTerm(e.target.value);
                              setIsCcDropdownOpen(true);
                            }}
                            onBlur={() => {
                              setTimeout(() => setIsCcDropdownOpen(false), 200);
                            }}
                            required
                          />
                          <span
                            className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                            style={{ fontSize: "11px", pointerEvents: "none" }}
                          >
                            ▼
                          </span>
                        </div>

                        {isCcDropdownOpen && (
                          <div
                            className="position-absolute start-0 end-0 shadow-lg overflow-auto mt-1"
                            style={{
                              maxHeight: "220px",
                              zIndex: 1050,
                              borderRadius: "10px",
                              background: "#ffffff",
                              border: "1px solid #cbd5e1",
                              left: "12px",
                              right: "12px",
                            }}
                          >
                            {filteredCcOperators.length === 0 ? (
                              <div className="px-3 py-2 text-muted f-s-13">No matching bank found</div>
                            ) : (
                              filteredCcOperators.map((op) => {
                                const isSel = cardIssuerOpcode === op.opcode;
                                return (
                                  <div
                                    key={op.opcode}
                                    className={`px-3 py-2 text-wrap f-s-13 border-bottom ${isSel ? "bg-primary text-white fw-bold" : "text-dark"}`}
                                    style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                    onMouseDown={() => {
                                      setCardIssuerOpcode(op.opcode);
                                      setCcSearchTerm(op.name);
                                      setIsCcDropdownOpen(false);
                                      setFetchedBill(null);
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSel) {
                                        e.currentTarget.style.background = "#ede9fe";
                                        e.currentTarget.style.borderLeft = "3px solid #7c3aed";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSel) {
                                        e.currentTarget.style.background = "transparent";
                                        e.currentTarget.style.borderLeft = "none";
                                      }
                                    }}
                                  >
                                    {op.name}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-dark f-s-14">16-Digit Card Number</label>
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                          maxLength="16"
                          placeholder="e.g. 4111222233334444"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, ""))}
                          required
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-dark f-s-14">Registered Mobile Number</label>
                        <input
                          type="tel"
                          className="form-control shadow-sm"
                          style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                          maxLength="10"
                          placeholder="e.g. 9876543210"
                          value={cardMobile}
                          onChange={(e) => setCardMobile(e.target.value.replace(/\D/g, ""))}
                          required
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mt-4 w-100">
                      <button
                        type="submit"
                        className="btn text-white px-4 py-2.5 fw-semibold"
                        style={{
                          background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                          borderRadius: "10px",
                          boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                          transition: "all 0.2s ease-in-out",
                          width: "auto",
                          minWidth: "180px",
                          maxWidth: "240px",
                          display: "inline-block",
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && cardNumber && cardMobile) {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 8px 20px rgba(124, 58, 237, 0.45)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(124, 58, 237, 0.3)";
                        }}
                        disabled={loading || !cardNumber || !cardMobile}
                      >
                        {loading ? "Fetching Card Bill..." : "Fetch Credit Card Bill"}
                      </button>
                    </div>
                  </form>
                )}

                {/* BILL PREVIEW CARD */}
                {fetchedBill && !receipt && (
                  <div className="card shadow-sm border-0 rounded-4 mt-4" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fffbebe6 100%)", borderLeft: "5px solid #f59e0b" }}>
                    <div className="card-body p-4">
                      <h5 className="card-title fw-bold text-dark mb-3">
                        <i className="ph-duotone ph-file-text me-2 text-warning f-s-20" />
                        Outstanding Bill Details
                      </h5>
                      <div className="row g-3 f-s-14">
                        {(fetchedBill.customer_name || fetchedBill.customerName) && (
                          <div className="col-md-6">
                            <span className="text-muted">Customer Name:</span>{" "}
                            <strong className="text-dark">{fetchedBill.customer_name || fetchedBill.customerName}</strong>
                          </div>
                        )}
                        {(fetchedBill.bill_number || fetchedBill.billNumber) && (
                          <div className="col-md-6">
                            <span className="text-muted">Bill Number:</span>{" "}
                            <strong className="text-dark">{fetchedBill.bill_number || fetchedBill.billNumber}</strong>
                          </div>
                        )}
                        {(fetchedBill.bill_date || fetchedBill.billDate) && (
                          <div className="col-md-6">
                            <span className="text-muted">Bill Date:</span>{" "}
                            <strong className="text-dark">{fetchedBill.bill_date || fetchedBill.billDate}</strong>
                          </div>
                        )}
                        {(fetchedBill.bill_due_date || fetchedBill.dueDate) && (
                          <div className="col-md-6">
                            <span className="text-muted">Due Date:</span>{" "}
                            <strong className="text-dark">{fetchedBill.bill_due_date || fetchedBill.dueDate}</strong>
                          </div>
                        )}
                        <div className="col-md-12 mt-2">
                          <span className="text-muted f-s-13">Amount Due</span>
                          <h2 className="text-danger fw-extrabold mb-0 mt-1">
                            ₹{money(fetchedBill.bill_amount || fetchedBill.due_amount || fetchedBill.amount)}
                          </h2>
                        </div>
                      </div>

                      <div className="text-end mt-4">
                        <button
                          type="button"
                          className="btn text-white px-5 py-2.5 fw-bold"
                          style={{
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            borderRadius: "12px",
                            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
                          }}
                          onClick={() => setShowPinModal(true)}
                        >
                          Proceed to Pay ₹{money(fetchedBill.bill_amount || fetchedBill.due_amount || fetchedBill.amount)}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 3: TRANSACTION STATUS CHECK */}
                {activeTab === "status" && (
                  <div>
                    <form onSubmit={handleCheckStatus} className="row g-2 mb-4" style={{ maxWidth: "540px" }}>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          className="form-control shadow-sm"
                          style={{ borderRadius: "10px", borderColor: "#cbd5e1" }}
                          placeholder="Enter Order ID (e.g. BPAY_... or CCF_...)"
                          value={statusOrderId}
                          onChange={(e) => setStatusOrderId(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-sm-4">
                        <button
                          type="submit"
                          className="btn text-white w-100 fw-semibold"
                          style={{
                            background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                            borderRadius: "10px",
                            boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                          }}
                          disabled={loading}
                        >
                          Check Status
                        </button>
                      </div>
                    </form>

                    {statusResult && (
                      <div className="card shadow-sm border-0 rounded-4 bg-light">
                        <div className="card-body p-4">
                          <h5 className="card-title fw-bold mb-3 text-dark">Transaction Status Details</h5>
                          <div className="row g-3 f-s-14">
                            <div className="col-md-6"><strong>Order ID:</strong> {statusResult.orderid || statusResult.order_id}</div>
                            <div className="col-md-6">
                              <strong>Status:</strong>{" "}
                              <span className={`badge px-3 py-1.5 ${statusResult.status === "success" ? "bg-success" : "bg-warning"}`}>
                                {statusResult.status || statusResult.provider?.status || "N/A"}
                              </span>
                            </div>
                            <div className="col-md-6"><strong>UTR / Reference:</strong> {statusResult.utr || "N/A"}</div>
                            <div className="col-md-6"><strong>TxID:</strong> {statusResult.txid || "N/A"}</div>
                            <div className="col-md-6"><strong>Amount:</strong> ₹{money(statusResult.amount)}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TRANSACTION PIN MODAL */}
      {showPinModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(6px)", zIndex: 3500 }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content shadow-lg rounded-4 border-0">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">Authorize Payment</h5>
                <button type="button" className="btn-close" onClick={() => setShowPinModal(false)}></button>
              </div>
              <div className="modal-body text-center py-4">
                <p className="text-muted f-s-13 mb-3">
                  Enter your transaction PIN to confirm the payment of{" "}
                  <strong className="text-primary">
                    ₹{money(fetchedBill?.bill_amount || fetchedBill?.due_amount || fetchedBill?.amount)}
                  </strong>.
                </p>
                <input
                  type="password"
                  className="form-control text-center f-s-20 fw-bold shadow-sm"
                  style={{ borderRadius: "10px", letterSpacing: "8px", borderColor: "#c4b5fd" }}
                  maxLength="6"
                  placeholder="PIN"
                  value={txnPin}
                  onChange={(e) => setTxnPin(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                />
              </div>
              <div className="modal-footer border-0 pt-0 d-flex gap-2">
                <button type="button" className="btn btn-light flex-grow-1 fw-semibold" style={{ borderRadius: "10px" }} onClick={() => setShowPinModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn text-white flex-grow-1 fw-bold"
                  style={{
                    background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && txnPin) {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 8px 18px rgba(124, 58, 237, 0.45)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(124, 58, 237, 0.3)";
                  }}
                  onClick={handlePayBill}
                  disabled={loading || !txnPin}
                >
                  {loading ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default BillPayment;
